/**
 * Why one price step — its drivers, the constraint that bound it, the elasticity and its version,
 * and the path AS ITS RUN WROTE IT (bata-ui#166 W4-2, `docs/adr/6126`, bata-platform ADR 6126).
 *
 * One read, `GET /inseason/runs/{run_id}/price-actions/{price_action_id}/explanation`, bounded by
 * the run in the URL and the step the planner opened. Every figure is the platform's:
 *
 *   - the drivers are `planning.markdown.optimise.drivers` over the solved path, signed, each in
 *     its own unit (money in the step's currency, units, markdown actions);
 *   - the binding constraint is what `planning.markdown.explain` found held the step back — or
 *     "no guardrail bound this step", which is a finding, not an absence;
 *   - the guardrails and the coefficient are the ones the run POSED the problem with, never
 *     today's registry; the fit row is the one at the step's own elasticity version;
 *   - the path is the steps the run wrote for the option × cluster, and — where the run recorded
 *     it — every solved week with its expected units.
 *
 * A run older than the record says so (`not_recorded_by_run`) and the written steps still show:
 * nothing here is re-solved (ADR 0014: absent is not zero).
 *
 * A zona-sales step is priced on calendar MONTHS (bata-reqs issue 35 P3, bata-platform ADR 6075):
 * under `period_grain = month` the three posed counters count months, not weeks, and the problem
 * states its stay — entered on `zona_sales_entry_month`, priced up to the month before
 * `terminal_month`. A step and a solved period name their month where `effective_month` is set
 * (`effective_week` is then the retail week its 1st falls in, kept so every older reader works).
 *
 * What the step was priced on (bata-reqs issue 35, `docs/adr/6024`): the posed problem's
 * `elasticity_basis` and `original_price_basis`, each drawn only where the run recorded it.
 */

import { useTranslation } from 'react-i18next';

import { priceStepExplanation } from '@/api/generated';
import type { PriceStepExplanationSummary, SolvedPathWeek, WrittenPriceStep } from '@/api/generated';
import { useApiQuery } from '@/api';
import { formatCurrency, formatNumber, formatPercent } from '@/lib/format';
import { AsyncRegion, DataTable, KpiBand } from '@/planning';
import { Badge, Measure, NumericCell, StatusIndicator } from '@/primitives';

import { NotPublishedNote } from './NotPublishedNote';
import { PriceBasisRows } from './PriceBasis';
import { PriceRoundingRows } from './PriceRounding';
import { roundingOf } from './price-rounding';
import { elasticityBasis, originalPriceBasis } from './price-basis';
import { isNotPublished } from './not-published';
import { ABSENT_MEASURE } from './panels';
import {
  guardrailPresentation, guardrailValue, priceConstraintKey, priceDriverPresentation, stepBinding,
  weekMoves, whyReasonKey,
} from './why';

export { monthName, stepWhen } from './month-name';
import { monthName, stepWhen } from './month-name';

function Section({ titleKey, children }: { readonly titleKey: string; readonly children: React.ReactNode }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="flex flex-col gap-2">
      <h5 className="text-label text-ink-secondary">{t(titleKey)}</h5>
      {children}
    </section>
  );
}

function Absent({ reason }: { readonly reason: string | null | undefined }): React.JSX.Element {
  const { t } = useTranslation();
  return <p className="text-caption text-ink-secondary">{t(whyReasonKey(reason))}</p>;
}

function Codes({ codes }: { readonly codes: readonly string[] }): React.JSX.Element {
  return (
    <ul className="flex flex-wrap gap-2">
      {codes.map((code) => (
        <li key={code} data-constraint={code}><Badge labelKey={priceConstraintKey(code)} /></li>
      ))}
    </ul>
  );
}

function Drivers({ why }: { readonly why: PriceStepExplanationSummary }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  if (why.explanation_absent_reason !== null) return <Absent reason={why.explanation_absent_reason} />;
  return (
    <ul className="flex flex-col gap-1">
      {why.drivers.map((driver) => {
        const shown = priceDriverPresentation(driver.code);
        const amount = shown.unit === 'money'
          ? formatCurrency(driver.contribution, why.currency_code, i18n.language)
          : formatNumber(driver.contribution, i18n.language);
        return (
          <li key={driver.code} className="flex items-baseline justify-between gap-4" data-driver={driver.code}>
            <span className="text-body text-ink-primary">
              {shown.known ? t(shown.labelKey) : t(shown.labelKey, { code: driver.code })}
            </span>
            <span className="font-numeric text-numeric text-end tabular-nums">
              {shown.unit === 'money' ? amount : t(
                shown.unit === 'actions' ? 'inseason.priceWhy.unit.actionsValue' : 'inseason.priceWhy.unit.unitsValue',
                { value: amount },
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Binding({ why }: { readonly why: PriceStepExplanationSummary }): React.JSX.Element {
  const { t } = useTranslation();
  const binding = stepBinding(why);
  return (
    <div className="flex flex-col gap-2">
      {binding.kind === 'unrecorded' && <Absent reason={why.explanation_absent_reason ?? 'not_recorded_by_run'} />}
      {binding.kind === 'free' && (
        <p className="text-body text-ink-primary">{t('inseason.priceWhy.noGuardrailBound')}</p>
      )}
      {binding.kind === 'bound' && <Codes codes={binding.codes} />}
      {why.path_constraints !== null && why.path_constraints.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="text-caption text-ink-secondary">{t('inseason.priceWhy.pathConstraints')}</p>
          <Codes codes={why.path_constraints} />
        </div>
      )}
    </div>
  );
}

function Guardrails({ why }: { readonly why: PriceStepExplanationSummary }): React.JSX.Element {
  return (
    <KpiBand>
      {why.guardrails.map((guardrail) => {
        const shown = guardrailPresentation(guardrail.key);
        return (
          <Measure descriptionKey="inseason.help.priceStepWhy.kpiGuardrail"
            key={guardrail.key}
            labelKey={shown.labelKey}
            unitKey={shown.unitKey}
            fractionDigits={shown.fractionDigits}
            value={guardrailValue(guardrail.key, guardrail.value)}
            absent={{ ...ABSENT_MEASURE, reasonKey: whyReasonKey(guardrail.absent_reason) }}
          />
        );
      })}
    </KpiBand>
  );
}

function Elasticity({ why }: { readonly why: PriceStepExplanationSummary }): React.JSX.Element {
  const { t } = useTranslation();
  const fit = why.elasticity_fit;
  const used = why.posed?.elasticity_coefficient ?? null;
  const notRecorded = { ...ABSENT_MEASURE, reasonKey: whyReasonKey(why.explanation_absent_reason) };
  const noFit = { ...ABSENT_MEASURE, reasonKey: whyReasonKey(why.elasticity_fit_absent_reason) };
  return (
    <div className="flex flex-col gap-2">
      <p className="text-caption text-ink-secondary" translate="no">
        {t('inseason.priceWhy.elasticityVersion', { version: why.elasticity_model_version_id })}
      </p>
      <KpiBand>
        <Measure descriptionKey="inseason.help.priceStepWhy.kpiCoefficientUsed" descriptionValues={{ version: why.elasticity_model_version_id }} labelKey="inseason.priceWhy.coefficientUsed" unitKey="inseason.priceWhy.unit.coefficient" fractionDigits={3} value={used} absent={notRecorded} />
        <Measure descriptionKey="inseason.help.priceStepWhy.kpiCoefficientFitted" descriptionValues={{ version: why.elasticity_model_version_id }} labelKey="inseason.priceWhy.coefficientFitted" unitKey="inseason.priceWhy.unit.coefficient" fractionDigits={3} value={fit?.coefficient ?? null} absent={noFit} />
        <Measure descriptionKey="inseason.help.priceStepWhy.kpiStandardError"
          labelKey="inseason.priceWhy.standardError" unitKey="inseason.priceWhy.unit.coefficient" fractionDigits={3}
          value={fit?.standard_error ?? null}
          absent={fit === null ? noFit : { ...ABSENT_MEASURE, reasonKey: 'inseason.priceWhy.precisionNotMeasured' }}
        />
        <Measure descriptionKey="inseason.help.priceStepWhy.kpiDegreesOfFreedom"
          labelKey="inseason.priceWhy.degreesOfFreedom" unitKey="inseason.priceWhy.unit.count" value={fit?.residual_degrees_of_freedom ?? null}
          absent={fit === null ? noFit : { ...ABSENT_MEASURE, reasonKey: 'inseason.priceWhy.precisionNotMeasured' }}
        />
        <Measure descriptionKey="inseason.help.priceStepWhy.kpiObservations" labelKey="inseason.priceWhy.observations" unitKey="inseason.priceWhy.unit.count" value={fit?.observations ?? null} absent={noFit} />
        <Measure descriptionKey="inseason.help.priceStepWhy.kpiOwnWeight"
          labelKey="inseason.priceWhy.ownWeight" unitKey="inseason.priceWhy.unit.percent"
          value={fit === null ? null : fit.own_weight * 100} absent={noFit}
        />
      </KpiBand>
      {fit !== null && (
        fit.within_sanity_band
          ? <StatusIndicator level="good" labelKey="inseason.priceWhy.withinSanityBand" />
          : <StatusIndicator level="warning" labelKey="inseason.priceWhy.outsideSanityBand" />
      )}
    </div>
  );
}

function Posed({ why }: { readonly why: PriceStepExplanationSummary }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const posed = why.posed;
  const absent = { ...ABSENT_MEASURE, reasonKey: whyReasonKey(why.explanation_absent_reason) };
  // ADR 6075: under `month` the three counters count months — labelled so, never as weeks.
  const monthly = posed?.period_grain === 'month';
  const entry = posed?.zona_sales_entry_month ?? null;
  const terminal = posed?.terminal_month ?? null;
  return (
    <div className="flex flex-col gap-2" data-period-grain={posed?.period_grain ?? ''}>
      <KpiBand>
        <Measure descriptionKey="inseason.help.priceStepWhy.kpiListPrice" descriptionValues={{ currency: why.currency_code }} labelKey="inseason.priceWhy.listPrice" unitKey="inseason.priceWhy.unit.currency" unitValues={{ currency: why.currency_code }} fractionDigits={2} value={posed?.list_price ?? null} absent={absent} />
        <Measure descriptionKey="inseason.help.priceStepWhy.kpiOpeningPrice" descriptionValues={{ currency: why.currency_code }} labelKey="inseason.priceWhy.openingPrice" unitKey="inseason.priceWhy.unit.currency" unitValues={{ currency: why.currency_code }} fractionDigits={2} value={posed?.opening_price ?? null} absent={absent} />
        <Measure descriptionKey="inseason.help.priceStepWhy.kpiOpeningUnits" labelKey="inseason.priceWhy.openingUnits" unitKey="inseason.unit.pairs" value={posed?.opening_units ?? null} absent={absent} />
        <Measure descriptionKey="inseason.help.priceStepWhy.kpiBaselineRate" labelKey="inseason.priceWhy.baselineRate" unitKey="inseason.priceWhy.unit.perWeek" fractionDigits={1} value={posed?.baseline_units_per_week ?? null} absent={absent} />
        {monthly ? (
          <>
            <Measure descriptionKey="inseason.help.priceStepWhy.kpiMonthsRemaining" labelKey="inseason.priceWhy.monthsRemaining" unitKey="inseason.priceWhy.unit.months" value={posed?.weeks_remaining ?? null} absent={absent} />
            <Measure descriptionKey="inseason.help.priceStepWhy.kpiMonthsOnSale" labelKey="inseason.priceWhy.monthsOnSale" unitKey="inseason.priceWhy.unit.months" value={posed?.weeks_on_sale ?? null} absent={absent} />
            <Measure descriptionKey="inseason.help.priceStepWhy.kpiMonthsSinceLastStep" labelKey="inseason.priceWhy.monthsSinceLastStep" unitKey="inseason.priceWhy.unit.months" value={posed?.weeks_since_last_step ?? null} absent={absent} />
          </>
        ) : (
          <>
            <Measure descriptionKey="inseason.help.priceStepWhy.kpiWeeksRemaining" labelKey="inseason.priceWhy.weeksRemaining" unitKey="inseason.priceWhy.unit.weeks" value={posed?.weeks_remaining ?? null} absent={absent} />
            <Measure descriptionKey="inseason.help.priceStepWhy.kpiWeeksOnSale" labelKey="inseason.priceWhy.weeksOnSale" unitKey="inseason.priceWhy.unit.weeks" value={posed?.weeks_on_sale ?? null} absent={absent} />
            <Measure descriptionKey="inseason.help.priceStepWhy.kpiWeeksSinceLastStep" labelKey="inseason.priceWhy.weeksSinceLastStep" unitKey="inseason.priceWhy.unit.weeks" value={posed?.weeks_since_last_step ?? null} absent={absent} />
          </>
        )}
      </KpiBand>
      {monthly && (
        <p className="text-caption text-ink-secondary max-w-prose" data-zona-sales-stay="">
          {t('inseason.priceWhy.zonaSalesStay', {
            entry: entry === null ? t('inseason.priceWhy.stayNotStated') : monthName(entry, i18n.language),
            terminal: terminal === null ? t('inseason.priceWhy.stayNotStated') : monthName(terminal, i18n.language),
          })}
        </p>
      )}
    </div>
  );
}

function WrittenSteps({ why }: { readonly why: PriceStepExplanationSummary }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <DataTable sourceKey="inseason.help.priceStepWhy.sourceWritten" helpValues={{ run: why.run_id }}
      rows={why.written_steps}
      rowKey={(step: WrittenPriceStep) => step.price_action_id}
      captionKey="inseason.priceWhy.writtenCaption"
      captionHidden={false}
      exportId="inseason.priceWhy.writtenSteps"
      exportTitleKey="inseason.priceWhy.writtenCaption"
      columns={[
        {
          id: 'effective_week', headerKey: 'inseason.priceWhy.column.week', descriptionKey: 'inseason.help.priceStepWhy.colWrittenWeek', kind: 'text',
          value: (step) => step.effective_week,
          cell: (step) => (
            <th scope="row" className="py-1 text-start font-normal" data-effective-month={step.effective_month ?? ''}>
              {step.price_action_id === why.price_action_id
                ? t('inseason.priceWhy.thisStep', { week: stepWhen(step, i18n.language) })
                : stepWhen(step, i18n.language)}
            </th>
          ),
        },
        {
          id: 'from_price', headerKey: 'inseason.priceDecision.detail.from', descriptionKey: 'inseason.help.priceStepWhy.colFrom', kind: 'number', align: 'end',
          value: (step) => step.from_price,
          cell: (step) => <td className="font-numeric text-numeric text-end tabular-nums">{formatCurrency(step.from_price, why.currency_code, i18n.language)}</td>,
        },
        {
          id: 'to_price', headerKey: 'inseason.priceDecision.detail.to', descriptionKey: 'inseason.help.priceStepWhy.colTo', kind: 'number', align: 'end',
          value: (step) => step.to_price,
          cell: (step) => <td className="font-numeric text-numeric text-end tabular-nums">{formatCurrency(step.to_price, why.currency_code, i18n.language)}</td>,
        },
        {
          id: 'depth_from_full_price', headerKey: 'inseason.priceDecision.detail.depth', descriptionKey: 'inseason.help.priceStepWhy.colDepth', kind: 'number', align: 'end',
          value: (step) => step.depth_from_full_price,
          cell: (step) => <td className="font-numeric text-numeric text-end tabular-nums">{formatPercent(step.depth_from_full_price, i18n.language)}</td>,
        },
      ]}
    />
  );
}

function SolvedWeeks({ why }: { readonly why: PriceStepExplanationSummary }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  if (why.explanation_absent_reason !== null) return <Absent reason={why.explanation_absent_reason} />;
  return (
    <DataTable sourceKey="inseason.help.priceStepWhy.sourcePath" helpValues={{ run: why.run_id }}
      rows={why.path_weeks}
      rowKey={(week: SolvedPathWeek) => String(week.week_index)}
      captionKey="inseason.priceWhy.pathCaption"
      captionHidden={false}
      exportId="inseason.priceWhy.pathWeeks"
      exportTitleKey="inseason.priceWhy.pathCaption"
      columns={[
        {
          id: 'period_key', headerKey: 'inseason.priceWhy.column.week', descriptionKey: 'inseason.help.priceStepWhy.colPathWeek', kind: 'text',
          value: (week) => week.period_key ?? '',
          cell: (week) => (
            <th scope="row" className="py-1 text-start font-normal">
              {week.effective_month !== null && week.effective_month !== undefined
                ? monthName(week.effective_month, i18n.language)
                : week.period_key ?? t('inseason.priceWhy.pastCalendar', { index: week.week_index + 1 })}
            </th>
          ),
        },
        {
          id: 'price', headerKey: 'inseason.priceWhy.column.price', descriptionKey: 'inseason.help.priceStepWhy.colPrice', kind: 'number', align: 'end',
          value: (week) => week.price,
          cell: (week) => <td className="font-numeric text-numeric text-end tabular-nums">{formatCurrency(week.price, why.currency_code, i18n.language)}</td>,
        },
        {
          id: 'expected_units', headerKey: 'inseason.priceWhy.column.expectedUnits', descriptionKey: 'inseason.help.priceStepWhy.colExpectedUnits', kind: 'number', align: 'end',
          value: (week) => week.expected_units,
          cell: (week) => <NumericCell absent={ABSENT_MEASURE} value={week.expected_units} />,
        },
        {
          id: 'opening_units', headerKey: 'inseason.priceWhy.column.openingUnits', descriptionKey: 'inseason.help.priceStepWhy.colOpeningUnits', kind: 'number', align: 'end',
          value: (week) => week.opening_units,
          cell: (week) => <NumericCell absent={ABSENT_MEASURE} value={week.opening_units} />,
        },
        {
          id: 'closing_units', headerKey: 'inseason.priceWhy.column.closingUnits', descriptionKey: 'inseason.help.priceStepWhy.colClosingUnits', kind: 'number', align: 'end',
          value: (week) => week.closing_units,
          cell: (week) => <NumericCell absent={ABSENT_MEASURE} value={week.closing_units} />,
        },
        {
          id: 'binding_constraints', headerKey: 'inseason.priceWhy.column.binding', descriptionKey: 'inseason.help.priceStepWhy.colBinding', kind: 'text',
          value: (week) => (week.binding_constraints ?? []).join(' '),
          cell: (week) => (
            <td>
              {!weekMoves(week) ? t('inseason.priceWhy.holds') : (
                (week.binding_constraints ?? []).length === 0
                  ? t('inseason.priceWhy.movesFreely')
                  : (week.binding_constraints ?? []).map((code) => t(priceConstraintKey(code), { code })).join(' · ')
              )}
            </td>
          ),
        },
      ]}
    />
  );
}

/** The composed "why" of one step, as the platform published it. */
export function PriceStepWhyView({ why }: { readonly why: PriceStepExplanationSummary }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4" data-price-why={why.price_action_id}>
      <Section titleKey="inseason.priceWhy.bindingTitle"><Binding why={why} /></Section>
      <Section titleKey="inseason.priceWhy.driversTitle"><Drivers why={why} /></Section>
      <Section titleKey="inseason.priceWhy.elasticityTitle"><Elasticity why={why} /></Section>
      {/* bata-reqs issue 35, ADR 6024: what the step was priced on, only where the run recorded it. */}
      {(elasticityBasis(why.posed?.elasticity_basis) !== null || originalPriceBasis(why.posed?.original_price_basis) !== null) && (
        <Section titleKey="inseason.priceBasis.title"><PriceBasisRows bases={why.posed} /></Section>
      )}
      {/* bata-reqs issue 38 R4, platform ADR 6190: how the price was rounded on the shelf. */}
      {roundingOf(why.rounding) !== null && (
        <Section titleKey="inseason.priceRounding.title">
          <PriceRoundingRows value={why.rounding} currency={why.currency_code} vatMultiplier={why.posed?.vat_multiplier} />
        </Section>
      )}
      <Section titleKey="inseason.priceWhy.guardrailsTitle"><Guardrails why={why} /></Section>
      <Section titleKey="inseason.priceWhy.posedTitle"><Posed why={why} /></Section>
      <Section titleKey="inseason.priceWhy.writtenTitle"><WrittenSteps why={why} /></Section>
      <Section titleKey="inseason.priceWhy.pathTitle"><SolvedWeeks why={why} /></Section>
    </div>
  );
}

/** The read, bounded by the run and the step; a platform that does not serve it yet says so. */
export function PriceStepWhy({ runId, priceActionId }: {
  readonly runId: string;
  readonly priceActionId: string;
}): React.JSX.Element {
  const query = useApiQuery(priceStepExplanation, { path: { run_id: runId, price_action_id: priceActionId } });
  if (query.isError && isNotPublished(query.error)) {
    return <NotPublishedNote bodyKey="inseason.priceWhy.notPublished" />;
  }
  return (
    <AsyncRegion
      query={query}
      errorTitleKey="inseason.priceWhy.failed"
      emptyTitleKey="inseason.priceWhy.emptyTitle"
      emptyBodyKey="inseason.priceWhy.emptyBody"
      loadingRows={4}
    >
      {(why: PriceStepExplanationSummary) => <PriceStepWhyView why={why} />}
    </AsyncRegion>
  );
}
